/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect, useRef } from 'react'
import { formatDistanceStrict } from 'date-fns'
import { es } from 'date-fns/locale'
import utcValues from 'utils/utc-values'
import { FiMoreVertical } from 'react-icons/fi'
import { RiDeleteBin6Line } from 'react-icons/ri'
import { IoIosLink } from 'react-icons/io'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import { TransparentButton } from 'components/common/button'
// import { MdEdit } from 'react-icons/md'
import useClickOutside from 'hooks/use-click-outside'
import { useLesson } from 'querys/lesson'
import { Typography } from 'common/typography'
import useSession from 'querys/session'

const AuthorStyled = styled.div`
  display: flex;
  max-height: 36px;
  align-items: center;
  justify-content: space-between;
  position: relative;
  .icon {
    /* position: absolute; */
    right: 0;
    color: #AEB7C1;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    outline: 0;
  }
  .tab {
    background: var(--basePrimary);
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
    border-radius: 8px;
    padding: 8px 16px;
    position: absolute;
    z-index: 2;
    right: 0;
    top: 30px;
    display: grid;
    grid-row-gap: 25px;
    align-items: center;
    button {
      background: none;
      color: var(--baseSecondary);
      border: none;
      cursor: pointer;
      outline: 0;
      font-size: 16px;
      display: flex;
      align-items: center;
    }
    button svg {
      margin-right: 16px;
    }
  }
  .author {
    display: flex;
    align-items: center;
    &__name {
      margin-right: 8px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
    }
    &__time {
      color: var(--neutral60);
    }
  }
  .author__avatar {
    margin-right: 2px;
    height: 100%;
    display: inherit;
    align-items: inherit;
    img {
      border-radius: 50%;
      object-fit: cover;
      margin-right: 6px;
    }
  }
  &:hover .share{
    display: flex;
  }
  .share {
    display: none;
    flex-grow: 1;
    justify-content: flex-end;
    margin-right: 10px;
    margin-bottom: 6px;
    padding: 0;
    align-items: flex-start;
    top: 6px;
    right: 39px;
    &-btn {
      color: var(--gray);
      &:hover{
        color: var(--primary);
      }
    }
    &:hover{
      display: flex;
    }
  }
  .clipboard {
    visibility: ${({ copied }) => (copied ? 'visibile' : 'hidden')};
    opacity: ${({ copied }) => (copied ? '1' : '0')};
    position: absolute;
    background-color: #E8EAEF;
    color: var(--basePrimary);
    font-size: 13px;
    text-align: center;
    padding: 4px 8px;
    border-radius: 6px;
    right: 0;
    top: -29px;
  }
  @media(min-width: 1024px){
    padding-right: .5rem;
    .share{
      margin-bottom: 3px;
    }
    .clipboard {
      right: initial;
      top: -30px;
      right: 34px;
    }
  }
`

function AuthorComment({
  author: { email, photo, first_name, last_name }, commentId, created_at, onDelete: handleDeleteComment,
}) {
  const [activeTab, setActiveTab] = useState(false)
  const [copied, setCopied] = useState(false)
  const menuRef = useRef(null)
  const commentRef = useRef(null)
  const { profile: { email: user } } = useSession()
  const router = useRouter()
  const {
    data: { title, meta },
  } = useLesson()
  const { course: course_slug, lesson_slug } = router.query
  function handleToModify() {
    setActiveTab(!activeTab)
  }
  function shareSuccess() {
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 4000)
  }
  function goToComment() {
    // const scroll = commentRef.current.offsetTop - 30
    // window.scrollTo(0, scroll)
    const commentUrl = `${window.location.origin}/aprender/${course_slug}/${lesson_slug}?featured=${commentId}`
    if ('share' in navigator) {
      navigator.share({
        title,
        text: meta,
        url: commentUrl,
      })
        .then(() => {
          shareSuccess()
        })
    } else {
      navigator.clipboard.writeText(commentUrl)
        .then(() => {
          shareSuccess()
        })
        .catch(() => {
          console.log('navigator clipboard not suported')
        })
    }
  }
  useEffect(() => {
    if (router.query.featured === commentId) {
      const scroll = commentRef.current?.offsetTop
      if (window.matchMedia('(min-width: 1024px)').matches) {
        const lessonContent = document.querySelector('.lesson-content')
        lessonContent.scrollTo(0, scroll - 100)
      } else {
        window.scrollTo(0, scroll - 75)
      }
    }
  }, [commentRef.current])
  useClickOutside(menuRef, handleToModify)
  const isAuthor = user === email
  const isSuperUser = user === 'mianse3@gmail.com' || user === 'leonidasesteban@gmail.com' || user === 'humbertolopez954@gmail.com'
  return (
    <AuthorStyled ref={commentRef} copied={copied}>
      <div className="author">
        <figure className="author__avatar">
          <img
            width="32"
            height="32"
            src={photo || '/icons/icon-50x50.png'}
            alt={`${first_name} ${last_name}`}
            loading="lazy"
          />
        </figure>
        <Typography className="author__name" small="p2Bold" large="regularBold" color="var(--baseNeutral)">
          {`${first_name && first_name} ${last_name && last_name}`}
        </Typography>
        <Typography className="author__time" small="smallRegular" large="p2Regular">
          {created_at
            ? `Hace ${formatDistanceStrict(utcValues(created_at), new Date(), { locale: es })}`
            : 'Hace 0 segundos'
          }
        </Typography>
      </div>
      <div className="share">
        <TransparentButton className="share-btn" onClick={() => goToComment()}>
          <IoIosLink size={20} color="var(--turquoise)" />
        </TransparentButton>
      </div>
      <div className="clipboard">
        <p className="clipboard-text">Url copiada al portapapeles</p>
      </div>
      {(isAuthor || isSuperUser) && (
        <button onClick={handleToModify} className="icon" type="button" data-cy="comment-menu-icon">
          <FiMoreVertical size={24} />
        </button>
      )}
      {activeTab && (
        <div className="tab" ref={menuRef} data-cy="comment-menu">
          {/* <button><MdEdit size={24} /> Editar</button> */}
          <button onClick={() => handleDeleteComment(commentId)} type="button">
            <RiDeleteBin6Line size={24} color="var(--baseSecondary)" />
            {'Eliminar'}
          </button>
        </div>
      )}
    </AuthorStyled>
  )
}

// AuthorComment.defaultProps = {
//   isReply: null,
// }

AuthorComment.propTypes = {
  commentId: PropTypes.string.isRequired,
  created_at: PropTypes.string.isRequired,
  onDelete: PropTypes.func.isRequired,
  author: PropTypes.shape({
    email: PropTypes.string,
    photo: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
  }).isRequired,
}

export default AuthorComment
